/**
 * Dialog system for the Talos SPA.
 *
 * Provides a context provider that renders stacked modal/non-modal dialogs and
 * exposes `useDialog()` returning `{ open }`. `open` accepts the same options
 * as the legacy dialog API and returns a handle with `close()`.
 */
import { createContext } from 'preact';
import { useContext, useState, useCallback } from 'preact/hooks';

const DialogCtx = createContext(null);

let dialogSeq = 0;

export function DialogProvider({ children }) {
  const [dialogs, setDialogs] = useState([]);

  const close = useCallback((id) => {
    setDialogs((list) => list.filter((d) => d.id !== id));
  }, []);

  const open = useCallback(
    (opts = {}) => {
      const id = ++dialogSeq;
      const modal = opts.modal !== false;
      const dismissible = opts.dismissible !== undefined ? opts.dismissible : !modal;
      const layer = { ...opts, id, modal, dismissible, close: () => close(id) };
      setDialogs((list) => [...list, layer]);
      return { id, close: layer.close };
    },
    [close]
  );

  const value = { open, closeAll: () => setDialogs([]) };

  return (
    <DialogCtx.Provider value={value}>
      {children}
      <div id="dialog-root" className="fixed inset-0 pointer-events-none z-50">
        {dialogs.map((d, idx) => (
          <DialogLayer key={d.id} dlg={d} zIndex={50 + idx * 10} />
        ))}
      </div>
    </DialogCtx.Provider>
  );
}

function DialogLayer({ dlg, zIndex }) {
  const { title = '', body = '', buttons = [], modal, dismissible, close } = dlg;

  const handleButton = (b) => {
    if (typeof b.onClick === 'function') b.onClick(dlg);
    if (b.closeOnClick !== false && b.onClick === undefined) close();
  };

  return (
    <div
      className="pointer-events-none fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: String(zIndex) }}
    >
      {modal && <div className="pointer-events-auto absolute inset-0 bg-black/50" />}
      <div
        role="dialog"
        aria-modal={modal ? 'true' : 'false'}
        className="pointer-events-auto relative w-full max-w-sm rounded-box bg-base-100 border border-base-300 shadow-xl"
      >
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-base-300">
          <h2 className="text-base font-semibold text-base-content">{title}</h2>
          {!modal && dismissible && (
            <button
              type="button"
              className="btn btn-ghost btn-xs btn-square text-base-content/50"
              onClick={close}
              aria-label="Close"
            >
              ✕
            </button>
          )}
        </div>
        <div className="px-4 py-4 text-sm text-base-content/80 overflow-y-auto max-h-[60vh]">
          {typeof body === 'string' ? body : body}
        </div>
        {buttons.length > 0 && (
          <div className="flex justify-end gap-2 px-4 py-3 border-t border-base-300">
            {buttons.map((b, i) => (
              <button
                key={i}
                type="button"
                className={b.className || 'btn btn-sm'}
                onClick={() => handleButton(b)}
              >
                {b.label ?? 'OK'}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function useDialog() {
  return useContext(DialogCtx);
}